import { Box, Link, type SxProps } from '@mui/material';
import {
  GridActionsCellItem,
  type GridCellParams,
  type GridColDef,
  type GridSortItem,
} from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import { getProjectPath } from '@verantos-dev/core-constants';
import { cohortQueryKeys, fetchCohortList } from '@verantos-dev/data-access-cohorts';
import { fetchProjectChats, projectPageKeys } from '@verantos-dev/feature-project-page';
import { type DataGridMessages, Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { type ProjectDetail } from '@verantos-dev/types-projects';

import { ProjectListActionMenu } from '../containers/ProjectListActionMenu/ProjectListActionMenu';
import { ProjectDialogType, useProjectModalContext } from '../context/modalContext';

const stringCellStyles: SxProps = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.2',
  whiteSpace: 'normal',
};

const clickableCellStyle: SxProps = {
  ...stringCellStyles,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const useProjectsListPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    user: { isInternalUser, organizationId: userOrganizationId },
  } = useAuth();

  const messages: DataGridMessages = {
    noRows: intl.formatMessage({ id: 'projectsList.datagrid.noRows' }),
    noResults: intl.formatMessage({ id: 'projectsList.datagrid.noResults' }),
    error: intl.formatMessage({ id: 'projectsList.datagrid.errorMessage' }),
  };

  const sortModel: GridSortItem[] = [{ field: 'modifiedOn', sort: 'desc' }];
  const { openDialog } = useProjectModalContext();

  const onStudyCellClick = (params: GridCellParams) => {
    // Skip handling for projectName field - it's handled by the Link component
    // This allows Ctrl+click and Shift+click to work properly
    if (params.field === 'projectName') {
      return;
    }
  };

  const columnDefinition: GridColDef<ProjectDetail>[] = [
    {
      headerName: intl.formatMessage({
        id: 'projectsList.columnHeader.name',
        defaultMessage: 'Name',
      }),
      type: 'string',
      field: 'projectName',
      flex: 1,
      minWidth: 330,
      renderCell: (params) => {
        const projectName = params?.row.projectName;
        const projectId = params?.row.projectId;
        // Lands on /projects/:projectId — when research_assistant_gen_3 is OFF
        // the route redirects to the cohorts list (see ProjectIndexElement in
        // projects-ui-routes); when ON it renders the agent-first ProjectPage.
        const href = getProjectPath(projectId);

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          // Allow Ctrl+click (Cmd+click on Mac) or Shift+click to open in new tab/window
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            return; // Let browser handle it naturally via href
          }
          e.preventDefault();
          // Prefetch cohorts so the legacy redirect path stays warm.
          // Fire-and-forget — mirrors the old RTK `initiate(...)` behaviour.
          void queryClient.prefetchQuery({
            queryKey: cohortQueryKeys.list(projectId),
            queryFn: () => fetchCohortList(projectId),
          });
          // Prefetch the project's "me" chat list so the agent-first
          // ProjectPage's welcome greeting (gated on this same query via
          // useWelcomeVariant) doesn't sit behind a cold GET on landing —
          // this click fires the request a beat before ProjectPage mounts
          // and re-triggers the identical query, so TanStack Query dedupes
          // to the one in-flight fetch instead of waiting on a second.
          void queryClient.prefetchQuery({
            queryKey: projectPageKeys.chatsForProject(projectId, 'me'),
            queryFn: () => fetchProjectChats({ projectId, ownerFilter: 'me' }),
          });
          navigate(href);
        };

        return (
          <Link
            component={RouterLink}
            to={href}
            data-testid={`project-name-${projectId}`}
            sx={clickableCellStyle}
            onClick={handleClick}
          >
            {projectName}
          </Link>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'projectsList.columnHeader.dataset' }),
      type: 'string',
      field: 'datasetReleaseName',
      flex: 1,
      renderCell: ({ row }) => {
        const { projectId, datasetReleaseName: datasetName, datasetReleaseId: datasetId } = row;

        const handleDatasetClick = () => {
          openDialog(ProjectDialogType.DATASET_DETAILS, null, { datasetId, datasetName });
        };

        return (
          <Box
            data-testid={`project-dataset-${projectId}`}
            sx={clickableCellStyle}
            onClick={handleDatasetClick}
          >
            {datasetName}
          </Box>
        );
      },
      valueGetter: (_, row) => {
        return row.datasetReleaseName || '';
      },
      minWidth: 330,
      sortComparator: (v1, v2) => v1.toString().localeCompare(v2.toString()),
    },
    ...(isInternalUser
      ? ([
          {
            headerName: intl.formatMessage({ id: 'projectsList.columnHeader.organization' }),
            type: 'string',
            field: 'organizationName',
            minWidth: 180,
            flex: 0.5,
            renderCell: ({ row }) => {
              const { projectId, organizationName } = row;
              return (
                <Box data-testid={`project-organization-${projectId}`} sx={stringCellStyles}>
                  {organizationName}
                </Box>
              );
            },
          },
          {
            headerName: intl.formatMessage({ id: 'projectsList.columnHeader.visibility' }),
            type: 'boolean',
            field: 'isVisible',
            minWidth: 180,
            flex: 0.5,
            renderCell: ({ row }) => {
              const { projectId, isVisible, organizationId } = row;
              return (
                <Box data-testid={`project-visibility-${projectId}`} sx={stringCellStyles}>
                  {organizationId !== userOrganizationId
                    ? intl.formatMessage({
                        id: `projectsList.visibility.${isVisible ? 'enabled' : 'disabled'}`,
                      })
                    : '--'}
                </Box>
              );
            },
          },
        ] as GridColDef<ProjectDetail>[])
      : []),
    {
      headerName: intl.formatMessage({ id: 'projectsList.columnHeader.modifiedOn' }),
      type: 'dateTime',
      field: 'modifiedOn',
      minWidth: 230,
      flex: 0.5,
      valueGetter: (_, row) => {
        return new Date(row.modifiedBy.transactionDate);
      },
      renderCell: (params) => {
        const modifiedOn = params.row.modifiedBy.transactionDate
          ? formatDate(params.row.modifiedBy.transactionDate, 'dateTimeFull')
          : '--';
        const projectId = params?.row.projectId;
        return (
          <Box data-testid={`cohort-owner-${projectId}`} sx={stringCellStyles}>
            {modifiedOn}
          </Box>
        );
      },
    },
    {
      field: '',
      headerName: intl.formatMessage({ id: 'common.grid.columnName.actions' }),
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',

      getActions: () => {
        return [
          <GridActionsCellItem
            icon={<Icon icon="edit" id="Edit" />}
            label="Edit"
            onClick={() => {
              alert('Edit project');
            }}
          />,
          <GridActionsCellItem
            icon={<Icon icon="delete" id="delete" />}
            label="Delete"
            onClick={() => {
              alert('Delete project');
            }}
          />,
        ];
      },
      renderCell: (param) => {
        return <ProjectListActionMenu project={param.row} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    sortModel,
    onStudyCellClick,
    columnDefinition,
    messages,
  };
};
